/**
 * The Log page's record of candidate emails the board has SENT.
 *
 * Deliberately the audit trail rather than a Lever note: an invite is a TA
 * action, and the team wanted one place that answers "did we already write to
 * this candidate, when, with what attached, and who sent it".
 */
import { useQuery } from '@tanstack/react-query';
import { Paperclip, RefreshCw } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { cn } from '../../../shared';
import { fetchSentEmails, type SentEmail } from '../services/taActionBoardApi';
import { isMine } from './logScope';

const KIND_LABEL: Record<string, string> = {
  'case-invite': 'Case invite',
  'link-resend': 'New booking link',
  'move-invite': 'Stage invite',
  invite: 'Invite',
};

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-GB', {
        timeZone: 'Europe/Berlin',
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
}

const fmtSize = (bytes: number | null) =>
  bytes ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : '';

/** `mine` scopes the list to emails the board sent on one recruiter's behalf. */
export function SentEmailsPanel({ mine }: { mine?: string | null } = {}) {
  const { data, isPending, error, refetch, isFetching } = useQuery({
    queryKey: ['ta-sent-emails'],
    queryFn: fetchSentEmails,
  });
  const all: SentEmail[] = data ?? [];
  const rows: SentEmail[] = mine ? all.filter((r) => isMine(r.sentBy, mine)) : all;

  return (
    <section className="mt-8">
      <header className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Sent candidate emails</h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Every email the Action Board sent on a recruiter's behalf, newest first — including what
            was attached.
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={() => void refetch()} disabled={isFetching}>
          <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', isFetching && 'animate-spin')} />
          Refresh
        </Button>
      </header>

      {error && (
        <p className="rounded-lg border border-[#F7C0CA] bg-[#FCDFE4] px-3 py-2 text-sm text-[#791F1F]">
          {error instanceof Error ? error.message : 'Could not load the sent-email log.'}
        </p>
      )}

      {isPending ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
          Nothing sent from the board yet.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl ring-1 ring-gray-200">
          <table className="w-full min-w-[46rem] text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 font-semibold">When</th>
                <th className="px-3 py-2 font-semibold">Candidate</th>
                <th className="px-3 py-2 font-semibold">Role / stage</th>
                <th className="px-3 py-2 font-semibold">Subject</th>
                <th className="px-3 py-2 font-semibold">Attachment</th>
                <th className="px-3 py-2 font-semibold">Sent by</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.opportunityId}-${r.sentAt}`} className="border-t border-gray-100">
                  <td className="whitespace-nowrap px-3 py-2 text-gray-500">{fmtWhen(r.sentAt)}</td>
                  <td className="px-3 py-2">
                    <span className="font-medium text-gray-900">{r.candidateName || r.to}</span>
                    <span className="block text-xs text-gray-400">{r.to}</span>
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {r.roleTitle || '—'}
                    <span className="block text-xs text-gray-400">
                      {KIND_LABEL[r.kind] || r.kind}
                      {r.stageName ? ` · ${r.stageName}` : ''}
                    </span>
                  </td>
                  <td className="max-w-[18rem] truncate px-3 py-2 text-gray-700" title={r.subject}>
                    {r.subject}
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {r.attachmentName ? (
                      <>
                        <Paperclip className="mr-1 inline h-3 w-3" />
                        {r.attachmentName}
                        <span className="block text-xs text-gray-400">{fmtSize(r.attachmentBytes)}</span>
                      </>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-gray-500">{r.sentBy}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
